import { useNavigate, useParams } from 'react-router-dom';

import { Tabs } from '../components/common';
import { AppShell } from '../components/layout/AppShell';
import { Sidebar } from '../components/layout/Sidebar';
import { TopBar } from '../components/layout/TopBar';
import { useConversations } from '../hooks/useConversations';
import { AttributesTab } from './settings/AttributesTab';
import { GeneralTab } from './settings/GeneralTab';
import { IntegrationsTab } from './settings/IntegrationsTab';
import { PromptsTab } from './settings/PromptsTab';
import { ServingTab } from './settings/ServingTab';
import { SystemTab } from './settings/SystemTab';
import { UsersTab } from './settings/UsersTab';
import { VersionsTab } from './settings/VersionsTab';
import './pages.css';

const TABS = [
  { id: 'prompts', label: 'Prompts' },
  { id: 'versions', label: 'Model versions' },
  { id: 'serving', label: 'Model serving' },
  { id: 'attributes', label: 'Attribute sets' },
  { id: 'users', label: 'Users' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'general', label: 'General' },
  { id: 'system', label: 'System' },
];

const SUBTITLES = {
  prompts:
    'Your own prompts. Save them, mark one as default, use {{variables}} — or ignore the library and type ad-hoc in the composer.',
  versions:
    'Checkpoints in the project folder, and the provenance each one needs: attribute set, response scheme, image preprocessing.',
  serving:
    'Which base models are on the GPU, and the policy for loading them: one model at a time, or several sharing the card.',
  attributes:
    'The attribute vocabularies. Order and value strings are part of the response contract, so these are read-only here.',
  users:
    'People in your organization, and the API keys their systems use. Each person sees only their own conversations; administrators see the whole organization.',
  integrations:
    'How other systems send images to POST /predict: which hosts the server may download image URLs from.',
  general:
    'Limits and defaults that apply without a restart: chat history, request sizes, fairness between organizations, sign-in and the default model version.',
  system: 'Worker, GPU, configuration and readiness.',
};

export function SettingsPage() {
  const { tab = 'prompts' } = useParams();
  const navigate = useNavigate();
  const conversations = useConversations();
  const active = TABS.some((candidate) => candidate.id === tab) ? tab : 'prompts';

  return (
    <AppShell
      sidebar={
        <Sidebar
          conversations={conversations.conversations}
          loading={conversations.loading}
          onNewAnalysis={() => navigate('/')}
          onSelectConversation={(id) => navigate(`/c/${id}`)}
          onDeleteConversation={conversations.remove}
        />
      }
    >
      <main className="page">
        <TopBar title="Settings" showVersion={false} />
        <div className="page__body">
          <div className="page__head">
            <Tabs tabs={TABS} active={active} onChange={(next) => navigate(`/settings/${next}`)} />
            <div className="page__subtitle" style={{ marginTop: 12 }}>
              {SUBTITLES[active]}
            </div>
          </div>

          {active === 'prompts' && <PromptsTab />}
          {active === 'versions' && <VersionsTab />}
          {active === 'serving' && <ServingTab />}
          {active === 'attributes' && <AttributesTab />}
          {active === 'users' && <UsersTab />}
          {active === 'integrations' && <IntegrationsTab />}
          {active === 'general' && <GeneralTab />}
          {active === 'system' && <SystemTab />}
        </div>
      </main>
    </AppShell>
  );
}
